import { MOCK_SPUR_ASSIGNED_PGS } from "../../data/mockPalletGroupData";

export function createInitialSpurAssignments() {
  return JSON.parse(JSON.stringify(MOCK_SPUR_ASSIGNED_PGS));
}

export function getAssignedPalletGroupsForSpur(spurRow, assignmentsBySpur) {
  if (!spurRow) {
    return [];
  }
  const map = assignmentsBySpur || MOCK_SPUR_ASSIGNED_PGS;
  if (spurRow.assignedPalletGroups?.length) {
    return spurRow.assignedPalletGroups;
  }
  return map[spurRow.spur] || [];
}

export function getEligibleReassignTargetSpurs(spurRows, sourceSpurKey) {
  return spurRows.filter(
    (row) => row.spur !== sourceSpurKey && row.spurStatus !== "Suspended",
  );
}

export function resolvePgsForReassign(pgIds, sourceSpur, assignmentsBySpur) {
  const assigned = getAssignedPalletGroupsForSpur(sourceSpur, assignmentsBySpur);
  const idSet = new Set(pgIds);
  return assigned.filter((pg) => idSet.has(pg.id));
}

export function formatCurrentlyAssignedPgsLabel(spurRow, assignmentsBySpur) {
  const pgs = getAssignedPalletGroupsForSpur(spurRow, assignmentsBySpur);
  if (pgs.length === 0) {
    return null;
  }
  return pgs.map((p) => p.palletGroup).join(", ");
}

export function mapSpurPgToUnassignedRow(pg) {
  const shipTo =
    pg.shipTo ||
    (pg.shipToCode && pg.shipToName
      ? `${pg.shipToCode}\n${pg.shipToName}`
      : undefined);

  return {
    id: pg.id,
    palletGroup: pg.palletGroup,
    type: pg.type || pg.pgType,
    pgType: pg.pgType || pg.type,
    inboundId: pg.inboundId,
    asn: pg.asn,
    shipToCode: pg.shipToCode,
    shipToName: pg.shipToName,
    shipTo,
    po: pg.po,
    totalILPNs: pg.totalILPNs,
    sortingRule: pg.sortingRule,
    ilpnIds: pg.ilpnIds || [],
    diverted: pg.diverted,
    palletised: pg.palletised,
  };
}

export function mapUnassignedRowToSpurPg(row) {
  return {
    id: row.id,
    palletGroup: row.palletGroup,
    asn: row.asn,
    shipToCode: row.shipToCode,
    shipToName: row.shipToName,
    po: row.po,
    inboundId: row.inboundId,
    totalILPNs: row.totalILPNs,
    diverted: row.diverted ?? 0,
    palletised: row.palletised ?? 0,
    sortingRule: row.sortingRule,
    type: row.type || row.pgType,
    pgType: row.pgType || row.type,
    ilpnIds: row.ilpnIds || [],
  };
}

function patchSpurRowFromAssignments(row, pgs) {
  if (pgs.length === 0) {
    const nextStatus =
      row.spurStatus === "Suspended" ? "Suspended" : "Idle";
    return {
      ...row,
      palletGroupCount: 0,
      palletGroup: "—",
      inboundId: "—",
      shipTo: "—",
      shipToCode: undefined,
      shipToName: undefined,
      totalCartons: 0,
      diverted: 0,
      palletised: 0,
      readyToComplete: false,
      spurStatus: nextStatus,
    };
  }

  const totalCartons = pgs.reduce((sum, p) => sum + p.totalILPNs, 0);
  const diverted = pgs.reduce((sum, p) => sum + (p.diverted || 0), 0);
  const palletised = pgs.reduce((sum, p) => sum + (p.palletised || 0), 0);
  const readyToComplete =
    pgs.length > 0 &&
    pgs.every((p) => p.totalILPNs > 0 && p.palletised === p.totalILPNs);

  const inboundIds = [
    ...new Set(pgs.map((p) => p.inboundId).filter(Boolean)),
  ];
  let inboundId = row.inboundId;
  if (inboundIds.length === 1) {
    inboundId = inboundIds[0];
  } else if (inboundIds.length > 1) {
    inboundId = `Mixed (${inboundIds.length})`;
  }

  let shipTo = row.shipTo;
  let shipToCode;
  let shipToName;
  if (pgs.length === 1) {
    shipToCode = pgs[0].shipToCode;
    shipToName = pgs[0].shipToName;
    shipTo = `${pgs[0].shipToCode}\n${pgs[0].shipToName}`;
  } else {
    const shipCodes = [...new Set(pgs.map((p) => p.shipToCode).filter(Boolean))];
    shipTo = shipCodes.length > 1 ? `Mixed (${shipCodes.length})` : row.shipTo;
  }

  let spurStatus = row.spurStatus;
  if (spurStatus === "Idle") {
    spurStatus = "Assigned";
  }

  return {
    ...row,
    palletGroupCount: pgs.length,
    palletGroup:
      pgs.length === 1
        ? pgs[0].palletGroup
        : `Multiple PGs (${pgs.length})`,
    inboundId,
    shipTo,
    shipToCode,
    shipToName,
    totalCartons,
    diverted,
    palletised,
    readyToComplete,
    spurStatus,
  };
}

/**
 * Assign unassigned PG rows to one spur (many→one) or one PG to many spurs (one→many).
 */
export function applyAssignPalletGroupsToSpurs({
  assignmentsBySpur,
  spurRows,
  palletGroupsToAssign,
  targetSpurKeys,
}) {
  if (!palletGroupsToAssign?.length || !targetSpurKeys?.length) {
    return null;
  }

  const isManyToMany =
    palletGroupsToAssign.length > 1 && targetSpurKeys.length > 1;
  if (isManyToMany) {
    return null;
  }

  const nextAssignments = { ...assignmentsBySpur };
  const assignedEntries = palletGroupsToAssign.map(mapUnassignedRowToSpurPg);

  if (targetSpurKeys.length === 1) {
    const spurKey = targetSpurKeys[0];
    const list = [...(nextAssignments[spurKey] || [])];
    const existingIds = new Set(list.map((p) => p.id));
    assignedEntries.forEach((pg) => {
      if (!existingIds.has(pg.id)) {
        list.push(pg);
        existingIds.add(pg.id);
      }
    });
    nextAssignments[spurKey] = list;
  } else {
    const pg = assignedEntries[0];
    if (!pg) {
      return null;
    }
    targetSpurKeys.forEach((spurKey) => {
      const list = [...(nextAssignments[spurKey] || [])];
      if (!list.some((p) => p.id === pg.id)) {
        list.push({ ...pg });
      }
      nextAssignments[spurKey] = list;
    });
  }

  const affectedSpurs = new Set(targetSpurKeys);
  const nextSpurRows = spurRows.map((row) => {
    if (!affectedSpurs.has(row.spur)) {
      return row;
    }
    const pgs = nextAssignments[row.spur] || [];
    return patchSpurRowFromAssignments(row, pgs);
  });

  return {
    assignmentsBySpur: nextAssignments,
    spurRows: nextSpurRows,
    assignedIds: assignedEntries.map((p) => p.id),
  };
}

export function applyUnassignFromSpur({
  assignmentsBySpur,
  spurRows,
  sourceSpurKey,
  pgIds,
  unassignedPalletGroups,
}) {
  const nextAssignments = { ...assignmentsBySpur };
  const sourceList = [...(nextAssignments[sourceSpurKey] || [])];
  const idSet = new Set(pgIds);

  const toUnassign = sourceList.filter((pg) => idSet.has(pg.id));
  const remaining = sourceList.filter((pg) => !idSet.has(pg.id));
  nextAssignments[sourceSpurKey] = remaining;

  const nextSpurRows = spurRows.map((row) => {
    if (row.spur !== sourceSpurKey) {
      return row;
    }
    return patchSpurRowFromAssignments(row, remaining);
  });

  const existingUnassignedIds = new Set(
    (unassignedPalletGroups || []).map((row) => row.id),
  );
  const mergedUnassigned = [...(unassignedPalletGroups || [])];
  toUnassign.forEach((pg) => {
    const row = mapSpurPgToUnassignedRow(pg);
    if (!existingUnassignedIds.has(row.id)) {
      mergedUnassigned.push(row);
      existingUnassignedIds.add(row.id);
    }
  });

  return {
    assignmentsBySpur: nextAssignments,
    spurRows: nextSpurRows,
    unassignedPalletGroups: mergedUnassigned,
    unassignedIds: toUnassign.map((pg) => pg.id),
  };
}

export function applyReassignToSpurs({
  assignmentsBySpur,
  spurRows,
  sourceSpurKey,
  targetSpurKey,
  pgIds,
}) {
  const nextAssignments = { ...assignmentsBySpur };
  const sourceList = [...(nextAssignments[sourceSpurKey] || [])];
  const targetList = [...(nextAssignments[targetSpurKey] || [])];
  const idSet = new Set(pgIds);

  const toMove = sourceList.filter((pg) => idSet.has(pg.id));
  const remaining = sourceList.filter((pg) => !idSet.has(pg.id));
  nextAssignments[sourceSpurKey] = remaining;

  const targetIdSet = new Set(targetList.map((p) => p.id));
  toMove.forEach((pg) => {
    if (!targetIdSet.has(pg.id)) {
      targetList.push(pg);
    }
  });
  nextAssignments[targetSpurKey] = targetList;

  const affectedSpurs = new Set([sourceSpurKey, targetSpurKey]);
  const nextSpurRows = spurRows.map((row) => {
    const inAssignmentMap = Object.prototype.hasOwnProperty.call(
      nextAssignments,
      row.spur,
    );
    if (!inAssignmentMap && !affectedSpurs.has(row.spur)) {
      return row;
    }
    const pgs = inAssignmentMap ? nextAssignments[row.spur] : [];
    return patchSpurRowFromAssignments(row, pgs);
  });

  return {
    assignmentsBySpur: nextAssignments,
    spurRows: nextSpurRows,
    movedPgs: toMove,
  };
}
